import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronRight, Home } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useAuth } from '../../context/AuthContext'

export interface BreadcrumbItem {
  label: string
  href?: string
}

interface BreadcrumbProps {
  items?: BreadcrumbItem[]
  className?: string
}

// Map route segments to human-readable labels
const segmentLabels: Record<string, string> = {
  dashboard: 'Dashboard',
  customers: 'Customers',
  marketplace: 'Marketplace',
  reports: 'Reports',
  settings: 'Settings',
  tasks: 'Tasks',
  storage: 'Storage',
  calendar: 'Calendar',
  proposals: 'Proposals',
  leads: 'Leads',
  projects: 'Projects',
  create: 'Create',
  edit: 'Edit',
  pipeline: 'Pipeline View',
  timeline: 'Timeline View',
  templates: 'Templates',
}

function generateBreadcrumbs(
  pathname: string,
  resolveDynamic?: (segment: string, parent: string | null) => string | null,
): BreadcrumbItem[] {
  const segments = pathname.split('/').filter(Boolean)
  const items: BreadcrumbItem[] = []

  let currentPath = ''
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`
    const isLast = index === segments.length - 1

    // Check if the segment is a known label
    const label = segmentLabels[segment]

    if (label) {
      items.push({
        label,
        href: isLast ? undefined : currentPath,
      })
    } else {
      // Dynamic segment (e.g. customer ID). Try to resolve to a friendly name first;
      // fall back to a truncated ID so we never show a full GUID.
      const parent = index > 0 ? segments[index - 1] : null
      const resolved = resolveDynamic?.(segment, parent)
      items.push({
        label: resolved ?? (segment.length > 12 ? `${segment.slice(0, 12)}...` : segment),
        href: isLast ? undefined : currentPath,
      })
    }
  })

  return items
}

export default function Breadcrumb({ items, className }: BreadcrumbProps) {
  const location = useLocation()
  // Pull managed customers so a `/customers/:id` segment resolves to the customer name
  // instead of leaving a raw GUID like "f0e02b20-1bd..." in the breadcrumb.
  const { managedCustomers } = useAuth()
  const resolveDynamic = React.useCallback(
    (segment: string, parent: string | null): string | null => {
      if (parent === 'customers') {
        const match = managedCustomers.find((c) => c.customerId === segment)
        if (match) return match.customerName
      }
      return null
    },
    [managedCustomers],
  )
  const breadcrumbs = items || generateBreadcrumbs(location.pathname, resolveDynamic)

  if (breadcrumbs.length === 0) return null

  return (
    <nav aria-label="Breadcrumb" className={cn('flex items-center', className)}>
      <ol className="flex items-center gap-1.5 text-sm">
        <li>
          <Link
            to="/dashboard"
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <Home className="h-4 w-4" />
          </Link>
        </li>
        {breadcrumbs.map((item, index) => (
          <li key={index} className="flex items-center gap-1.5">
            <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
            {item.href ? (
              <Link
                to={item.href}
                className="text-slate-500 hover:text-slate-900 transition-colors font-medium"
              >
                {item.label}
              </Link>
            ) : (
              <span className="text-slate-900 font-medium">{item.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  )
}
